import {
  ProductBehavior,
  ProductEventActorKind,
  type ProductEventActorDto,
  type ProductEventChangeDto,
  type ProductEventDto,
  ProductEventType,
} from '@up2stay/up2promo-api-client';

/**
 * Product history (DEV-755): workspace-audience product events → timeline
 * rows. Pure, so the template choice is unit-tested without React.
 */

/** Copy keys under `products.detail.history`. */
export type ProductHistoryTemplate =
  | 'claimedEmail'
  | 'claimedEmailPublic'
  | 'claimedManual'
  | 'claimedAdmin'
  | 'claimedSetup'
  | 'targetChanged'
  | 'labelChanged'
  | 'feedbackOn'
  | 'feedbackOff'
  | 'locationCleared'
  | 'locationClearedUnnamed'
  | 'usageReset'
  | 'released';

export type ProductHistoryIcon =
  | 'claimed'
  | 'target'
  | 'label'
  | 'feedback'
  | 'location'
  | 'usage'
  | 'released';

export type ProductHistoryTarget = {
  kind: ProductBehavior;
  url?: string;
  userId?: string;
  locationId?: string;
};

/** A value rendered in bold: plain text, or something the UI describes. */
export type ProductHistoryValue =
  | { kind: 'text'; text: string }
  | { kind: 'target'; target: ProductHistoryTarget | null }
  | { kind: 'location'; locationId: string }
  | { kind: 'stars'; count: number };

export type ProductHistoryActor =
  | { kind: 'user'; name: string }
  | { kind: 'deleted' | 'support' | 'system' | 'public' };

export type ProductHistoryEntry = {
  /** Unique per row (an event with several changes yields several rows). */
  id: string;
  icon: ProductHistoryIcon;
  template: ProductHistoryTemplate;
  actor: ProductHistoryActor;
  values: Record<string, ProductHistoryValue>;
  /** CLAIMED: the target and name the workspace started with. */
  start?: { target: ProductHistoryValue; label: ProductHistoryValue };
  occurredAt: string;
};

const CLAIMED_VIA_EMAIL = 'CLAIM_EMAIL';
const CLAIMED_VIA_MANUAL = 'CLAIM_MANUAL';
const DIRECT_REVIEW_MODE = 'direct_review';

export function toHistoryActor(
  actor: ProductEventActorDto,
): ProductHistoryActor {
  switch (actor.kind) {
    case ProductEventActorKind.PLATFORM_ADMIN:
      return { kind: 'support' };
    case ProductEventActorKind.PUBLIC:
      return { kind: 'public' };
    case ProductEventActorKind.USER: {
      const name = actor.displayName?.trim();
      if (actor.deleted || !name) return { kind: 'deleted' };
      return { kind: 'user', name };
    }
    default:
      return { kind: 'system' };
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isBehavior(value: unknown): value is ProductBehavior {
  return Object.values(ProductBehavior).includes(value as ProductBehavior);
}

function asTarget(value: unknown): ProductHistoryTarget | null {
  if (!isRecord(value) || !isBehavior(value.kind)) return null;
  const str = (key: string) => {
    const raw = value[key];
    return typeof raw === 'string' && raw ? raw : undefined;
  };
  return {
    kind: value.kind,
    url: str('url'),
    userId: str('userId'),
    locationId: str('locationId'),
  };
}

function text(value: unknown): ProductHistoryValue {
  return {
    kind: 'text',
    text: typeof value === 'string' && value.trim() ? value.trim() : '—',
  };
}

function findChange(
  changes: readonly ProductEventChangeDto[],
  field: string,
): ProductEventChangeDto | undefined {
  return changes.find((change) => change.field === field);
}

/** On when a non-default funnel filters before Google (ADR-0026 §3). */
function feedbackFilter(value: unknown): { on: boolean; stars: number } {
  if (!isRecord(value) || value.mode === DIRECT_REVIEW_MODE) {
    return { on: false, stars: 0 };
  }
  const stars =
    typeof value.reviewThreshold === 'number' ? value.reviewThreshold : 0;
  return { on: true, stars };
}

function claimedTemplate(
  event: ProductEventDto,
  actor: ProductHistoryActor,
): ProductHistoryTemplate {
  const via = event.metadata?.claimedVia;
  if (via === CLAIMED_VIA_EMAIL) {
    // The setup contact confirms by link, so the actor is anonymous.
    return actor.kind === 'public' ? 'claimedEmailPublic' : 'claimedEmail';
  }
  if (via === CLAIMED_VIA_MANUAL) return 'claimedManual';
  // Admin assign / move carry no metadata; a member's create is the setup.
  if (actor.kind === 'user' || actor.kind === 'deleted') return 'claimedSetup';
  return 'claimedAdmin';
}

/** Timeline rows for one event, in display order. */
export function toHistoryEntries(
  event: ProductEventDto,
): ProductHistoryEntry[] {
  const actor = toHistoryActor(event.actor);
  const row = (
    icon: ProductHistoryIcon,
    template: ProductHistoryTemplate,
    values: Record<string, ProductHistoryValue> = {},
    id = event.id,
  ): ProductHistoryEntry => ({
    id,
    icon,
    template,
    actor,
    values,
    occurredAt: event.occurredAt,
  });

  switch (event.type) {
    case ProductEventType.CLAIMED: {
      const target = findChange(event.changes, 'target');
      const label = findChange(event.changes, 'meta.label');
      const entry = row('claimed', claimedTemplate(event, actor));
      if (target || label) {
        entry.start = {
          target: { kind: 'target', target: asTarget(target?.to) },
          label: text(label?.to),
        };
      }
      return [entry];
    }
    case ProductEventType.UPDATED:
      return event.changes.flatMap((change) => {
        if (change.field === 'target') {
          return [
            row(
              'target',
              'targetChanged',
              {
                from: { kind: 'target', target: asTarget(change.from) },
                to: { kind: 'target', target: asTarget(change.to) },
              },
              `${event.id}:target`,
            ),
          ];
        }
        if (change.field === 'meta.label') {
          return [
            row(
              'label',
              'labelChanged',
              { from: text(change.from), to: text(change.to) },
              `${event.id}:label`,
            ),
          ];
        }
        return [];
      });
    case ProductEventType.FEEDBACK_CHANGED: {
      const filter = feedbackFilter(findChange(event.changes, 'feedback')?.to);
      return filter.on
        ? [
            row('feedback', 'feedbackOn', {
              stars: { kind: 'stars', count: filter.stars },
            }),
          ]
        : [row('feedback', 'feedbackOff')];
    }
    case ProductEventType.LOCATION_CLEARED: {
      const locationId = asTarget(
        findChange(event.changes, 'target')?.from,
      )?.locationId;
      return locationId
        ? [
            row('location', 'locationCleared', {
              location: { kind: 'location', locationId },
            }),
          ]
        : [row('location', 'locationClearedUnnamed')];
    }
    case ProductEventType.USAGE_RESET:
      return [row('usage', 'usageReset')];
    case ProductEventType.RELEASED:
      // Only reachable when this workspace owned the product before and owns
      // it again: the history never shows another workspace's events.
      return [row('released', 'released')];
    default:
      return [];
  }
}

/** Rows for a newest-first event list (pages concatenated). */
export function buildHistoryEntries(
  events: readonly ProductEventDto[],
): ProductHistoryEntry[] {
  return events.flatMap(toHistoryEntries);
}
